/** leads/types.ts — 리드 모듈 타입 (교차참조 타입은 한 파일 유지 원칙) */

export type LeadStatus =
  | 'unassigned' | 'assigned' | 'contacting' | 'consulting'
  | 'in_progress' | 'won' | 'lost' | 'hold';

export const STATUS_LABEL: Record<LeadStatus, string> = {
  unassigned: '미배정', assigned: '배정', contacting: '방문완료', consulting: '상담중',
  in_progress: '진행중', won: '성공', lost: '실패', hold: '보류', // 방문(돌방) 실무 반영 (2026-07-17)
};

/** 기존 모듈 Badge 관례: 옅은 배경 + 컬러 글자 (성공만 solid 강조) — tm ui.tsx C 토큰 */
export const STATUS_STYLE: Record<LeadStatus, { bg: string; fg: string }> = {
  unassigned: { bg: '#F3F4F6', fg: '#4B5563' },
  assigned: { bg: '#EFF6FF', fg: '#2563EB' },
  contacting: { bg: '#FFF7ED', fg: '#EA580C' },
  consulting: { bg: '#F5F3FF', fg: '#7C3AED' },
  in_progress: { bg: '#F0FDF4', fg: '#16A34A' },
  won: { bg: '#16A34A', fg: '#FFFFFF' },
  lost: { bg: '#FEF2F2', fg: '#DC2626' },
  hold: { bg: '#F3F4F6', fg: '#9CA3AF' },
};

// 버그수정(2026-08-11): 유니온 밖 상태값(신규 상태·레거시 activity의 status_to)으로 인덱싱 시
//   STATUS_STYLE[x].bg 가 undefined 참조로 화면 크래시. 폴백 제공 헬퍼로 안전 접근.
export function stStyle(s: string | null | undefined): { bg: string; fg: string } {
  return (s && STATUS_STYLE[s as LeadStatus]) || { bg: '#E5E7EB', fg: '#374151' };
}
export function stLabel(s: string | null | undefined): string {
  return (s && STATUS_LABEL[s as LeadStatus]) || String(s || '');
}

export interface EligibleYear { year: number; deadline: string; daysLeft: number }

export interface FpNote { id: string; author_name: string; content: string; created_at: string }

export interface VentureDetail {
  invest_year: number | null;
  invest_amount: number | null;
  venture_from: string | null;
  venture_to: string | null;
  eligible_years: EligibleYear[];
  // 만료류 유형(venture_expiry·mainbiz_expiry·innobiz_expiry) 확장 필드
  expire_date?: string | null;
  cert_kind?: string;
  industry?: string;
  confirm_type?: string;
  estb_dt?: string | null;
  estb_year?: number | null;   // 신규법인(venture_new) 설립연도 — 벤처확인 기한 = 설립연도+2년 12월 31일 (2026-10-17)
}

/** 투자 건별(회차) — round 0 = FSC 연 합산 추정(회차 미상), 1..n = 수동 확정 회차 (2026-08-26) */
export interface InvestRound { id: string; amount: number; no: number; date?: string | null; src?: string | null }

export interface Lead {
  returned_at?: string | null; // 반납 이력 — 미배정과 구분 표시 (053)
  lost_at?: string | null; lost_reason?: string | null; lost_by_name?: string | null; // 실패-반납 (056)
  id: string;
  lead_type: string;
  biz_no: string;
  corp_name: string;
  ceo_name: string;
  address: string;
  phone: string;
  email: string;
  memo: string;
  corp_no?: string | null;
  detail: VentureDetail;
  status: LeadStatus;
  assigned_fp_id: string | null;
  assigned_branch: string | null;
  fp_name?: string | null;
  fp_company?: string | null;
  company_id: string | null;
  fs_year?: number | null;  // 스캔이 사업자번호로 채운 최신 결산 (2026-08-19)
  fs_sale?: number | null;  // 매출액
  fs_op?: number | null;    // 영업이익
  venture_periods?: { f: string; t: string | null; r: string }[] | null; // 벤처 유효기간 전체 이력 (2026-08-19)
  invest_rounds?: InvestRound[] | null; // 투자 건별(회차) — §16 건별 귀속연도 선택 대응 (2026-08-26)
  created_at: string;
  updated_at: string;
}

/** 회사 1행(사업자번호 묶음) — ATV 경정청구 대상 리스트와 같은 열 (2026-09-10) */
export interface CompanyLeadLite {
  id: string; invest_year: number | null; invest_amount: number | null; status: LeadStatus; fp_name?: string | null;
  assigned_fp_id: string | null; assigned_branch: string | null; accepted_at?: string | null; assigned_at?: string | null;
  returned_at?: string | null; lost_at?: string | null; lost_reason?: string | null; lost_by_name?: string | null;
  eligible_years: EligibleYear[]; invest_rounds?: InvestRound[] | null; refund_est: number; matched: boolean; phone?: string;
}
export interface CompanyRow {
  row_type: 'company'; key: string; id: string; lead_ids: string[];
  biz_no: string; corp_name: string; ceo_name: string; phone: string; address: string; region?: string; sigungu?: string;
  industry: string; estb_year: string; corp_no?: string | null; memo?: string;
  fs_year: number | null; fs_sale: number | null; fs_op: number | null;
  amount_total: number; years: number[]; rounds: number; refund_total: number; refund_assumed: boolean; deadline: string | null;
  venture_periods?: { f: string; t: string | null; r: string }[] | null;
  fp_names: string[]; fp_name: string | null; assigned_fp_id: string | null; assigned_branch: string | null;
  status: LeadStatus; statuses: Record<string, number>; pending_count: number; matched_count: number;
  returned_at?: string | null; lost_at?: string | null; lost_reason?: string | null; lost_by_name?: string | null;
  accepted_at?: string | null; assigned_at?: string | null; created_at: string | null; updated_at: string | null;
  leads: CompanyLeadLite[];
}
export type ListRow = (Lead & { row_type: 'lead'; industry?: string; estb_year?: string; refund_est?: number; amount_total?: number; next_deadline?: string | null; accepted_at?: string | null }) | CompanyRow;
export interface AssignCounts { total: number; unassigned: number; assigned: number; pending: number }

export interface LeadActivity {
  id: string;
  author_type: 'fp' | 'admin';
  author_name: string;
  kind: 'call' | 'visit' | 'memo' | 'status' | 'claim' | 'assign' | 'return' | 'contact' | 'meeting'; // meeting: 리드포탈 토탈 n차 결과 (2026-07-18)
  content: string;
  status_to: LeadStatus | null;
  created_at: string;
}

export interface MetaFp {
  id: string; name: string; phone: string; company: string;
  cart_size: number; active_count: number;
  kind?: 'fp' | 'wm'; // WM 배정 대상 (052)
}

export interface LeadMeta {
  regions: string[];
  sigungus: { region: string; sigungu: string }[];
  branches: string[];
  fps: MetaFp[];
  wms?: { id: string; name: string; phone: string; company: string; cart_size?: number; active_count?: number }[]; // WM 목록 (052) + 정원·진행수 (2026-07-17)
  counts: Partial<Record<LeadStatus, number>>;
}

export interface CartInfo { size: number; used: number; remaining: number; pending?: number; committed?: number } // used = 관리 DB(수락한 건) · pending = 수락 대기 · remaining = 정원 − (used+pending) (2026-09-10)

/** 요약재무제표 1개년 (금융위 API, 단위 원 — 화면에서 만원 환산) (v4 2026-09-05) */
export interface FsYear { year: number; bas_dt?: string; sale: number | null; op: number | null; np: number | null; pretax?: number | null; asset: number | null; debt: number | null; equity: number | null; capital: number | null; debt_ratio: number | null; retained?: number | null } // 금융위 요약재무제표 9항목 + 이익잉여금(재무상태표 API), 실측 확인 2026-09-05
export interface FsSummary { years: FsYear[]; increases: { year: number; diff: number }[]; reason: '' | 'no_corp_no' | 'none' }

export interface LeadListFilters {
  view?: 'company' | 'lead';   // 회사 1행(ATV 동일) / 리드 1행 (2026-09-10)
  sort?: 'refund' | 'amount' | 'deadline' | 'recent';
  assigned?: 'all' | 'unassigned' | 'assigned' | 'pending'; // 배정 여부
  year?: number;               // 특정 투자연도
  refundMin?: number; refundMax?: number; amountMin?: number; amountMax?: number; // 원 단위
  saleMin?: number; saleMax?: number; opMin?: number; opMax?: number; opLoss?: boolean;
  rounds?: number[];           // 증자 건수 1~5(5+)
  industry?: string; // 업종 (2026-07-18)
  region?: string;
  sigungu?: string;
  page: number;
  pageSize: number;
  status?: LeadStatus;
  q?: string;
  branch?: string;
  fp_id?: string;
  deadline_months?: number;
  has_phone?: 'yes' | 'no';
  invest_years?: number[]; // 투자연도 개수 다중선택 (회사 단위 증자연도 수, 5=5개 이상) (2026-08-19)
}

export interface LeadRowInput {
  biz_no: string; corp_name: string; ceo_name?: string; address?: string;
  phone?: string; email?: string; memo?: string;
  detail: { invest_year: number; invest_amount?: number | null; venture_from?: string | null; venture_to?: string | null };
}

/** FP 포털용 (마스킹 반영) */
export interface PortalLead {
  id: string; corp_name: string; status: LeadStatus; mine: boolean;
  lead_type?: string;
  assigned_at?: string | null;  // 배정일 (serializeForFp 가 내려줌 — 캐스팅 우회 제거, v4 2026-09-05)
  accepted?: boolean;           // 수락 여부 (097): 본인 배정 + 미수락 = '수락하기' 탭
  accepted_at?: string | null;  // 수락일
  estimated_refund?: number;    // 환급 예상(원, 과표 1억 가정) — 카드 블록 (2026-09-10)
  proposal_ready?: boolean;     // 제안서 생성 가능 (경정청구 유형 + 투자금액 + 가능연도)
  meeting_count?: number;       // 미팅 결과 등록 횟수 — 카드 안내 문구 분기
  memo?: string;                // 지도사 메모 (본인 배정분만)
  biz_no: string; ceo_name: string; address: string; phone: string; email: string;
  fp_notes: FpNote[];
  corp_no: string;
  detail: { invest_year: number | null; invest_amount: number | null; eligible_years: EligibleYear[]; expire_date?: string; cert_kind?: string; industry?: string; confirm_type?: string; venture_from?: string; venture_to?: string; estb_dt?: string; estb_year?: number | null };
  updated_at: string;
}

/** 벤처 후보군 (공공데이터 명단 — 사업자번호 미보유 모수, 회사 단위 그룹) */
export interface CandidatePeriod { id: string; venture_from: string; venture_to: string | null; renew_type: string }

export interface Candidate {
  corp_no?: string | null;
  corp_no_est?: boolean;
  id: string; corp_name: string; ceo_masked: string; confirm_type: string;
  region: string; sigungu: string; address: string; industry: string; product: string;
  venture_from: string; venture_to: string | null; renew_type: string;
  source_name: string; lead_id: string | null; created_at: string;
  periods: CandidatePeriod[];   // 연도별 파일 누적 — 최신순
  converted: boolean;           // 회사 단위 전환 여부 (어느 연도든)
  biz_no?: string;
  ceo_name?: string; // 대표자 실명(개요 API 백필) — 있으면 마스킹 대신 표시 (2026-08-19)
  estb_dt?: string | null;
  renew_status?: string | null; renew_end?: string | null; // 갱신 확인 (중소벤처24 API, 054)
  fp_name?: string; lead_assigned?: boolean; // 배정자 표시 (2026-07-18)
  fs_year?: number | null;
  fs_sale?: number | null;
  fs_op?: number | null;
}

/** 증자 업체 (①탭) — 벤처 대조 매칭 상태 포함 */
export interface Capital {
  id: string; biz_no: string | null; corp_no: string | null; corp_name: string; match_status: string;
  increase_year: number; increase_date: string | null; amount: number | null;
  address: string; source_name: string;
  matched_candidate_id: string | null; lead_id: string | null;
  vc_from?: string | null; vc_to?: string | null; vc_region?: string; vc_sigungu?: string;
}

export interface CapitalMeta { total: number; matched: number; needs_biz_no: number; leads: number; out_period: number; years: number[]; regions: string[]; sigungus: { region: string; sigungu: string }[] }

export interface ScanStatus {
  running: boolean; total: number; scanned: number; increases: number; outPeriod: number; leads: number; crnoFound: number;
  mode?: 'full' | 'crno' | 'fs';
  ambiguous: number; noCrno: number; noFs: number; errors: number; pausedByQuota: boolean; lastCorp: string;
  quota: { date: string; outline: number; fs: number; cap: number }; // API별 독립 쿼터 (개요/재무)
  remaining: number;
  lastRun: { started_at: string; finished_at: string | null; outcome: string; trigger: string;
    region: string; sigungu: string; stats: Record<string, number> } | null;
}

export interface ScanSchedule { enabled: boolean; region: string; sigungu: string; run_hour: number; last_run_date: string | null }

export interface CandidateMeta {
  counts: Record<string, number>;
  regions: string[];
  sigungus: { region: string; sigungu: string }[];
  total: number;
  converted: number;
  sources: { name: string; count: number }[];
}

export interface CandidateFilters {
  page: number; pageSize: number;
  q?: string; region?: string; sigungu?: string; confirm_type?: string;
  expiring_months?: number; converted?: 'all' | 'yes' | 'no'; corp_no?: 'has' | 'none';
  expired_recent?: boolean; // 만료 경과 3개월 내 (ATV 「만료 경과」, 2026-09-10)
  saleMin?: number; saleMax?: number; opMin?: number; opMax?: number; opLoss?: boolean; // 재무 조건 (원)
}

/** 증자 업체 회사당 1행 (view=company, ATV 「자본금 증자업체」 동일, 2026-09-10) */
export interface CapitalCompany {
  ckey: string; corp_name: string; biz_no: string | null; corp_no: string | null; address: string; region: string | null; sigungu: string | null;
  years: number[]; amount_total: number | null; cnt: number; has_lead: boolean; needs_biz_no: boolean; out_period: boolean; ambiguous: boolean; last_year: number;
  items: { id: string; year: number; amount: number | null; date: string | null; lead_id: string | null; matched: string | null; status: string; round_no: number }[];
}

export interface UploadAnomaly { corp_name: string; venture_from: string; field: string; existing: string; incoming: string }

/** 경정청구 제안서 데이터 (GET /lead-portal/:token/leads/:id/proposal, 2026-09-10) */
export interface ProposalContact { role: string; name: string; phone: string }
export interface ProposalClaimYear { year: number; label: string; due: string; limit: string; deadline: string; days_left: number; status: string; note: string }
export interface ProposalData {
  kind?: 'refund';
  lead_id: string; client_name: string; ceo_name: string; biz_no: string; industry: string;
  invest_year: number | null; invest_won: number; invest_manwon: number; invests: ProposalInvest[];
  invest_count: number; invest_total_won: number; invest_total_manwon: number;
  unknown_years: number[]; shared_claim_years: number[]; merge_claim_years: number[];
  venture_from: string; venture_to: string; claim_years: ProposalClaimYear[];
  author: string; org: string; today: string; contacts: ProposalContact[];
  estimated_refund_won: number; estimated_refund_total_won: number; estimated_refund_merged_won: number; tax_base_assumed_manwon: number;
}
/** 투자 1건(= 납입연도 1개, 같은 해 여러 회차는 합산) — 건마다 청구 연도를 따로 고른다 (조특법 §16①) */
export interface ProposalInvest {
  year: number; lead_id: string; won: number; manwon: number; is_current: boolean;
  deduction_manwon: number; refund_won: number;
  claim_years: { year: number; due: string; deadline: string; days_left: number }[];
  later_years: number[];
}
/** 링크 보내기 미리보기 (GET /leads/fp-portals/:id/send-link/preview) */
/** 담당자 보유 DB (회수 대상) — GET /fp-portals/:id/leads (2026-09-10) */
export interface HeldLead { id: string; client_name: string; ceo_name: string; biz_no: string; region: string; status: LeadStatus; assigned_at: string | null; accepted: boolean; accepted_at: string | null; last_update: string | null; invest_year: number | null; invest_amount: number }
export interface HeldLeads { fp_id: string; name: string; kind: 'fp' | 'wm'; rows: HeldLead[] }
export interface SendLinkPreview { id: string; name: string; kind: 'fp' | 'wm'; company: string; to: string; to_valid: boolean; url: string; full_url: string; message: string; bytes: number; msg_type: 'SMS' | 'LMS' }
export interface ProposalSettings { org: string; contacts: ProposalContact[] }
